import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  ConfidenceRow,
  NextThemeProvider,
  ScoreBar,
  SidebarItem,
  SkipcarToast,
  StatCard,
  StatusChip,
  sxCard,
  sxCardAccent,
  sxTableHead,
  sxTopbar,
  tokens,
} from '../theme-next';

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

// Ligne "document" telle qu'elle apparaîtra dans la colonne gauche du FormCGIA v2.
// Sera factorisée dans Chantier 3. Pour l'instant c'est un pattern visuel ici
// pour valider le rendu et les couleurs.
type DocStatus = 'ok' | 'alerte' | 'verifier' | 'pending';

const statusVisuals: Record<DocStatus, { bg: string; fg: string; icon: string; chipStatus: Parameters<typeof StatusChip>[0]['status'] }> = {
  ok: { bg: tokens.color.greenLight, fg: tokens.color.green, icon: '✓', chipStatus: 'autoValidated' },
  alerte: { bg: tokens.color.orangeLight, fg: tokens.color.orange, icon: '!', chipStatus: 'toVerify' },
  verifier: { bg: tokens.color.amberLight, fg: tokens.color.amberDeep, icon: '!', chipStatus: 'toVerify' },
  pending: { bg: tokens.color.surface, fg: tokens.color.textMuted, icon: '·', chipStatus: 'pending' },
};

const DocumentListItem = ({
  title,
  subtitle,
  status,
}: {
  title: string;
  subtitle: string;
  status: DocStatus;
}) => {
  const v = statusVisuals[status];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '12px 14px',
        borderBottom: `0.5px solid ${tokens.color.border}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          backgroundColor: v.bg,
          color: v.fg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: 800,
          flexShrink: 0,
          marginTop: '2px',
        }}
      >
        {v.icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.color.dark }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.color.textMuted, display: 'block', lineHeight: 1.4 }}>
          {subtitle}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <StatusChip status={v.chipStatus} />
      </Box>
    </Box>
  );
};

const ThemeNextSandbox = () => (
  <NextThemeProvider>
    <Box sx={{ p: 4, backgroundColor: tokens.color.bg, minHeight: '100vh' }}>
      <Typography variant="h2" sx={{ mb: 4 }}>
        Theme next-ui — Sandbox
      </Typography>

      <Section title="StatusChip">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <StatusChip status="draft" />
          <StatusChip status="pending" />
          <StatusChip status="toVerify" />
          <StatusChip status="critical" />
          <StatusChip status="blocked" />
          <StatusChip status="submitted" />
          <StatusChip status="delivered" />
          <StatusChip status="autoValidated" />
        </Stack>
      </Section>

      <Section title="ScoreBar">
        <Stack spacing={2} sx={{ maxWidth: 420 }}>
          <ScoreBar score={95} />
          <ScoreBar score={78} />
          <ScoreBar score={52} />
          <ScoreBar score={28} />
        </Stack>
      </Section>

      <Section title="StatCard">
        <Stack direction="row" spacing={2}>
          <StatCard label="Documents" value="9/9" sub="complets" accent="green" />
          <StatCard label="Alertes" value="3" sub="à traiter" accent="orange" />
          <StatCard label="Fiabilité" value="96%" sub="seuil 85%" accent="yellow" />
          <StatCard label="Bloqués" value="1" sub="action requise" accent="red" />
        </Stack>
      </Section>

      <Section title="ConfidenceRow">
        <Card sx={sxCard}>
          <ConfidenceRow field="Immatriculation" value="FW-502-XK" confidence={99} />
          <ConfidenceRow field="VIN" value="UU1DJF0036691…" confidence={97} />
          <ConfidenceRow field="Kilométrage cession" value="~71 300 km" confidence={72} warn />
          <ConfidenceRow field="Permis de conduire" value="Expirée 07/2021" confidence={52} warn />
        </Card>
      </Section>

      <Section title="Colonne gauche documents (cf. brief Image #1)">
        <Card sx={{ ...sxCard, maxWidth: 420 }}>
          <Box sx={{ px: '14px', py: '10px', borderBottom: `0.5px solid ${tokens.color.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="overline">Documents</Typography>
            <Typography variant="caption" sx={{ color: tokens.color.green, fontWeight: 700 }}>9 / 9 complets</Typography>
          </Box>
          <DocumentListItem title="Carte grise" subtitle="Mention cédée 07/04/2026 · signature présente" status="ok" />
          <DocumentListItem title="Certificat de cession" subtitle="Cerfa 15776*02 · date mois incohérente" status="alerte" />
          <DocumentListItem title="Cerfa 13750 — demande immat" subtitle="Signé Gazeran · 07/04/2026" status="ok" />
          <DocumentListItem title="Mandat Cerfa 13757" subtitle="SKIPCAR mandaté · VIN + immat corrects" status="ok" />
          <DocumentListItem title="Attestation assurance" subtitle="Active Assurances / AXA · effet 07/04/2026" status="ok" />
          <DocumentListItem title="Contrôle technique DEKRA" subtitle="03/03/2026 · favorable · valide 02/03/2028" status="ok" />
          <DocumentListItem title="CNI — CASTILLO Elena Beatriz" subtitle="N° 110778400871 · née 21/03/1978 Maubeuge" status="ok" />
          <DocumentListItem title="Permis de conduire" subtitle="Format livret · carte verso exp. 07/07/2021" status="verifier" />
          <DocumentListItem title="Justificatif de domicile" subtitle="Facture Sosh 26/03/2026 · 78610 Auffargis" status="ok" />
        </Card>
      </Section>

      <Section title="SidebarItem (dark)">
        <Box sx={{ backgroundColor: tokens.color.dark, p: 1, borderRadius: 2, maxWidth: 260 }}>
          <SidebarItem label="Dashboard" active count={2} countAlert icon={<span>●</span>} />
          <SidebarItem label="Dossiers" count={14} icon={<span>●</span>} />
          <SidebarItem label="À vérifier" icon={<span>●</span>} />
        </Box>
      </Section>

      <Section title="SkipcarToast">
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <SkipcarToast message="Document validé" sub="CNI OK" variant="success" />
          <SkipcarToast message="Kilométrage à confirmer" variant="warning" />
          <SkipcarToast message="Erreur réseau" variant="error" />
        </Stack>
      </Section>

      <Section title="Boutons">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="contained" color="primary">Soumettre</Button>
          <Button variant="outlined" color="inherit">Sauvegarder</Button>
          <Button variant="contained" color="error">Redemander</Button>
          <Button variant="contained" color="secondary">Voir le dossier</Button>
          <Button variant="outlined" color="inherit" size="small">Annuler</Button>
        </Stack>
      </Section>

      <Section title="Cards et helpers sx">
        <Stack direction="row" spacing={2}>
          <Card sx={sxCard}>
            <CardContent>
              <Typography variant="h4">sxCard neutre</Typography>
              <Typography variant="body2">Bordure fine, shadow card subtile.</Typography>
            </CardContent>
          </Card>
          <Card sx={sxCardAccent(tokens.color.red)}>
            <CardContent>
              <Typography variant="h4">sxCardAccent(red)</Typography>
              <Typography variant="body2">Bandeau gauche rouge.</Typography>
            </CardContent>
          </Card>
        </Stack>
      </Section>

      <Section title="Topbar + TableHead (MUI, pas HTML natif)">
        <Box sx={sxTopbar}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>skipcar</Typography>
          <Typography variant="caption" sx={{ color: tokens.color.textMuted }}>/ Carte grise / Dossier #38247</Typography>
        </Box>
        <Card sx={{ ...sxCard, mt: 2 }}>
          <TableContainer>
            <Table>
              <TableHead sx={sxTableHead}>
                <TableRow>
                  <TableCell>Immat</TableCell>
                  <TableCell>Dossier</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>FW-502-XK</TableCell>
                  <TableCell>#38247</TableCell>
                  <TableCell><StatusChip status="toVerify" /></TableCell>
                  <TableCell sx={{ maxWidth: 180 }}><ScoreBar score={81} /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Section>
    </Box>
  </NextThemeProvider>
);

export default ThemeNextSandbox;
